import { useCallback } from 'react'
import { useClipboard } from '@/shared/hooks/useClipboard'
import { useTranslations } from 'next-intl'
import type { DropdownItemWithKey, PrivilegesDropdownProps } from '@/shared/v2/shared/components/PrivilegesDropdown'
import PrivilegesDropdown from '@/shared/v2/shared/components/PrivilegesDropdown'

export type TableDropdownActionsProps<T extends Array<DropdownItemWithKey> = Array<DropdownItemWithKey>> = {
    dataToCopy?: string
    actions?: T
    authorizedActions?: T[number]['key'][]
    requiredPrivilege?: PrivilegesDropdownProps['requiredPrivilege']
    unauthorizedMessage?: PrivilegesDropdownProps['unauthorizedMessage']
}

const COPY_ACTION_KEY = 'copyToClipboard' as const

const TableDropdownActions = <T extends Array<DropdownItemWithKey>>({
    dataToCopy,
    actions,
    authorizedActions,
    requiredPrivilege,
    unauthorizedMessage
}: TableDropdownActionsProps<T>) => {
    const rootTranslations = useTranslations('root')
    const { onSetCopyStatus } = useClipboard()

    const onCopyToClipboard = useCallback(() => {
        onSetCopyStatus(dataToCopy)
    }, [dataToCopy, onSetCopyStatus])

    const allItems: DropdownItemWithKey[] = [
        ...(dataToCopy
            ? [
                  {
                      key: COPY_ACTION_KEY,
                      label: rootTranslations('components.copyId'),
                      action: onCopyToClipboard
                  }
              ]
            : []),
        ...(actions || [])
    ] as const

    return (
        <PrivilegesDropdown
            data-testid="__table-dropdown-actions__"
            intent="transparent"
            items={allItems}
            authorizedActions={authorizedActions}
            requiredPrivilege={requiredPrivilege}
            unauthorizedMessage={unauthorizedMessage}
        />
    )
}

export default TableDropdownActions
